import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  checkInAttendance,
  checkOutAttendance,
  createAttendance,
  deleteAttendance,
  editAttendance,
  getAttendances,
  getAttendanceSettings,
  getDailyAttendanceSummary,
  getDailySummaryEmployees,
  getMyAttendanceHistory,
  getTodayAttendance,
  updateAttendanceSettings,
} from "api/attendanceApi"
import {
  useCreateResource,
  useDeleteResource,
  useEditResource,
  usePaginatedResource,
} from "./useCrudResource"

export const ATTENDANCE_QUERY_KEY = "attendances"

export const useAttendances = (params = {}, enabled = true) =>
  usePaginatedResource(ATTENDANCE_QUERY_KEY, getAttendances, params, enabled)

export const useCreateAttendance = () =>
  useCreateResource(createAttendance, ATTENDANCE_QUERY_KEY)

export const useEditAttendance = () =>
  useEditResource(editAttendance, ATTENDANCE_QUERY_KEY)

export const useDeleteAttendance = () =>
  useDeleteResource(deleteAttendance, ATTENDANCE_QUERY_KEY)

export const useTodayAttendance = () =>
  useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, "today"],
    queryFn: getTodayAttendance,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })

export const useMyAttendanceHistory = (params = {}) =>
  useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, "my-history", JSON.stringify(params)],
    queryFn: () => getMyAttendanceHistory(params),
    keepPreviousData: true,
  })

export const useCheckIn = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: checkInAttendance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_QUERY_KEY] })
    },
  })
}

export const useCheckOut = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: checkOutAttendance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_QUERY_KEY] })
    },
  })
}

export const useDailyAttendanceSummary = (params = {}, enabled = true) =>
  useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, "summary", JSON.stringify(params)],
    queryFn: () => getDailyAttendanceSummary(params),
    enabled,
  })

export const useDailySummaryEmployees = (params = {}, enabled = false) =>
  useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, "summary-employees", JSON.stringify(params)],
    queryFn: () => getDailySummaryEmployees(params),
    enabled,
  })

export const useAttendanceSettings = (enabled = true) =>
  useQuery({
    queryKey: [ATTENDANCE_QUERY_KEY, "settings"],
    queryFn: getAttendanceSettings,
    enabled,
  })

export const useUpdateAttendanceSettings = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateAttendanceSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ATTENDANCE_QUERY_KEY] })
    },
  })
}
